'use client'
/**
 * PL counterpart of the legacy `withCustomSectionWrapper` HOC: mounts `CustomSectionWrapper`
 * (the section-level IS_VISIBLE / CLICK emitter) around a PL widget's rendered section.
 *
 * Legacy wraps EVERY section via `SectionRenderer`. PL widgets mount section components
 * directly, so this wrapper restores the emitter — gap D1 in the home-PL analytics parity work.
 * The dispatched TrackingEvent bubbles to `useTrackingEventCapturer` in the root layout
 * (`dynamic-client-root-layout.tsx`), which turns it into the IS_VISIBLE telemetry event —
 * the capturer is page-agnostic, so only the emitter side was missing on PL.
 *
 * Mounted whenever a trackingId resolves — matching legacy, where the emitter's click listener
 * fires `setCustomSectionFindingMethods` (home finding-method / campaign attribution, consumed by
 * later PDP + add-to-cart events) on ANY section click even when `trackingTypes` is empty.
 * `trackingTypes` (wire-shipped from proto-registry 3.20.0) only gates which TrackingEvents
 * dispatch: the emitter arms IS_VISIBLE on 'VISIBLE' and the section CLICK event on 'CLICK'.
 * Today's payloads carry a trackingId but no trackingTypes, so the wrapper adds the same inert
 * emitter `<div>` legacy has around every section, restoring click attribution now; the
 * IS_VISIBLE/CLICK events start flowing when the PL service populates `trackingTypes`.
 */

import { useMemo } from 'react'
import { isMobile } from 'react-device-detect'

import type CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import { getIsHomePage } from '@src/quince-site-common/src/utils/getPageType'
import CustomSectionWrapper from '@src/SectionRenderer/sections/CustomSectionWrapper'
import { HERO_BANNER_TRACKING_ID } from '@src/SectionRenderer/sections/withCustomSectionWrapper'

import type { PropsWithChildren } from 'react'

interface PLSectionTrackingWrapperProps {
  /** The widget's built section config (the same object handed to the section component). */
  sectionConfig: CustomSection
  /** Page-scoped tracker (e.g. 'home_page') — feeds setCustomSectionFindingMethods on click. */
  tracker?: string
}

export function PLSectionTrackingWrapper({
  sectionConfig,
  tracker,
  children,
}: Readonly<PropsWithChildren<PLSectionTrackingWrapperProps>>) {
  const trackingMetaData = sectionConfig.trackingMetaData
  const context = trackingMetaData?.context
  // PL builders don't all hoist these to the section top level; the tracking context carries the
  // same values (both mirror the legacy BFF payload), so fall back to it field by field.
  const trackingId = sectionConfig.trackingId ?? context?.trackingId
  const trackingTypes = sectionConfig.trackingTypes

  // Mobile homepage hero carousel reports as hero-banner-1 — mirrors withCustomSectionWrapper.
  const isMobileHeroCarousel = isMobile && trackingId === 'HERO_CAROUSEL' && getIsHomePage()
  const effectiveTrackingId = isMobileHeroCarousel ? HERO_BANNER_TRACKING_ID : trackingId
  const effectiveTrackingMetaData = useMemo(
    () =>
      isMobileHeroCarousel
        ? { ...trackingMetaData, context: { ...trackingMetaData?.context, trackingId: HERO_BANNER_TRACKING_ID } }
        : trackingMetaData,
    [trackingMetaData, isMobileHeroCarousel],
  )

  // No trackingId → the emitter could never attach anything (it self-gates and leaves its ref
  // unused), so skip the wrapper div entirely. trackingTypes is deliberately NOT part of this
  // gate: legacy fires the finding-method click side effect on trackingId alone.
  if (!trackingId) return <>{children}</>

  return (
    <CustomSectionWrapper
      trackingId={effectiveTrackingId}
      trackingTypes={trackingTypes}
      contentContainerType={sectionConfig.contentContainerType ?? context?.contentContainerType}
      contentContainerName={sectionConfig.contentContainerName ?? context?.contentContainerName}
      id={sectionConfig.id}
      genericContents={sectionConfig.genericContents}
      elementPosition={sectionConfig.elementPosition ?? context?.contentContainerPosition}
      // Legacy sections carry `displayTitle` (it lands in the telemetry context); the PL wire
      // mirrors that value as `analytics.contentContainerDisplayName` for widgets whose data has
      // no top-level displayTitle (swimlanes, half-banner, IRL).
      displayTitle={sectionConfig.displayTitle ?? context?.contentContainerDisplayName}
      trackingMetaData={effectiveTrackingMetaData}
      params={{ tracker }}
    >
      {children}
    </CustomSectionWrapper>
  )
}
